import type { Appointment, AppointmentParticipant } from '@medplum/fhirtypes';
import { CANCELLATION_REASON_SYSTEM } from '@unify/fhir-model/constants';
import type { CancellationReasonCode } from '@unify/fhir-model/resources/appointment';
import {
  getPatientParticipants,
  setParticipantAttendanceStatus,
} from '@unify/fhir-model/resources/appointment';

export type AppointmentWorkflowStatus =
  | 'requested'
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'late-cancelled'
  | 'no-show';

export type AppointmentWorkflowStatusOption = {
  value: AppointmentWorkflowStatus;
  label: string;
  description: string;
  disabled?: boolean;
};

const WORKFLOW_LABELS: Record<AppointmentWorkflowStatus, string> = {
  requested: 'Requested',
  confirmed: 'Scheduled',
  completed: 'Attended',
  cancelled: 'Cancelled',
  'late-cancelled': 'Late Cancelled',
  'no-show': 'No-Show',
};

const WORKFLOW_DESCRIPTIONS: Record<AppointmentWorkflowStatus, string> = {
  requested: 'Requested but not yet confirmed, e.g. client portal request',
  confirmed: 'Scheduled and on the calendar',
  completed: 'Session attended successfully',
  cancelled: 'Appointment was cancelled',
  'late-cancelled': 'Client cancelled late; session did not occur',
  'no-show': 'Client did not attend',
};

const ALL_WORKFLOW_STATUSES: AppointmentWorkflowStatus[] = [
  'requested',
  'confirmed',
  'completed',
  'cancelled',
  'late-cancelled',
  'no-show',
];

const LATE_CANCEL_DISPLAY = 'Client Late Cancelled';

/** Calendar UI status values (provider calendar / saveCalendarAppointment). */
export type CalendarAppointmentStatus =
  | 'proposed'
  | 'scheduled'
  | 'attended'
  | 'confirmed'
  | 'completed'
  | 'client-cancelled'
  | 'client-late-cancelled'
  | 'clinician-cancelled'
  | 'client-no-show';

const CALENDAR_TO_WORKFLOW: Record<string, AppointmentWorkflowStatus> = {
  proposed: 'requested',
  scheduled: 'confirmed',
  attended: 'completed',
  confirmed: 'confirmed',
  completed: 'completed',
  'client-cancelled': 'cancelled',
  'clinician-cancelled': 'cancelled',
  'client-late-cancelled': 'late-cancelled',
  'client-no-show': 'no-show',
};

export function calendarStatusToWorkflowStatus(
  calendarStatus: string | null | undefined,
): AppointmentWorkflowStatus {
  const key = calendarStatus ?? 'scheduled';
  return CALENDAR_TO_WORKFLOW[key] ?? 'confirmed';
}

export function getCalendarStatusTransitionDisabledReason(
  currentCalendarStatus: string,
  targetCalendarStatus: string,
): string | undefined {
  if (currentCalendarStatus === targetCalendarStatus) return undefined;
  if (targetCalendarStatus !== 'proposed') return undefined;
  const current = calendarStatusToWorkflowStatus(currentCalendarStatus);
  if (current === 'requested') return undefined;
  return `Cannot change to ${WORKFLOW_LABELS.requested}`;
}

export function buildCalendarStatusSelectData(
  savedCalendarStatus: string,
  options: ReadonlyArray<{ value: string; label: string }>,
): Array<{ value: string; label: string; disabled?: boolean }> {
  return options.map((option) => {
    const disabled =
      getCalendarStatusTransitionDisabledReason(savedCalendarStatus, option.value) != null;
    return {
      value: option.value,
      label: option.label,
      disabled,
    };
  });
}

export function getAppointmentCancelationReasonCode(
  appointment: Appointment,
): CancellationReasonCode | undefined {
  return appointment.cancelationReason?.coding?.find(
    (c) => c.system === CANCELLATION_REASON_SYSTEM,
  )?.code as CancellationReasonCode | undefined;
}

export function getAppointmentWorkflowStatus(appointment: Appointment): AppointmentWorkflowStatus {
  const status = appointment.status;
  if (status === 'proposed') return 'requested';
  if (
    status === 'booked'
    || status === 'arrived'
    || status === 'checked-in'
    || status === 'pending'
    || status === 'waitlist'
  ) {
    return 'confirmed';
  }
  if (status === 'fulfilled') return 'completed';
  if (status === 'noshow') return 'no-show';
  if (status === 'cancelled') {
    if (getAppointmentCancelationReasonCode(appointment) === 'client-late-cancelled') {
      return 'late-cancelled';
    }
    return 'cancelled';
  }
  return 'confirmed';
}

export function getAppointmentWorkflowStatusLabel(appointment: Appointment): string {
  return WORKFLOW_LABELS[getAppointmentWorkflowStatus(appointment)];
}

const WORKFLOW_BADGE_COLORS: Record<AppointmentWorkflowStatus, string> = {
  requested: 'violet',
  confirmed: 'blue',
  completed: 'green',
  cancelled: 'red',
  'late-cancelled': 'orange',
  'no-show': 'red',
};

export function getWorkflowStatusBadgeColor(status: AppointmentWorkflowStatus): string {
  return WORKFLOW_BADGE_COLORS[status];
}

export function getAppointmentWorkflowStatusBadgeColor(appointment: Appointment): string {
  return getWorkflowStatusBadgeColor(getAppointmentWorkflowStatus(appointment));
}

export function buildAppointmentStatusPatch(
  appointment: Appointment,
  next: AppointmentWorkflowStatus,
): Appointment {
  const patch: Appointment = { ...appointment };

  switch (next) {
    case 'requested':
      patch.status = 'proposed';
      patch.cancelationReason = undefined;
      break;
    case 'confirmed':
      patch.status = 'booked';
      patch.cancelationReason = undefined;
      break;
    case 'completed':
      patch.status = 'fulfilled';
      patch.cancelationReason = undefined;
      break;
    case 'cancelled':
      patch.status = 'cancelled';
      patch.cancelationReason = undefined;
      break;
    case 'late-cancelled':
      patch.status = 'cancelled';
      patch.cancelationReason = {
        coding: [
          {
            system: CANCELLATION_REASON_SYSTEM,
            code: 'client-late-cancelled',
            display: LATE_CANCEL_DISPLAY,
          },
        ],
      };
      break;
    case 'no-show':
      patch.status = 'noshow';
      patch.cancelationReason = undefined;
      break;
  }

  return patch;
}

export function getAppointmentTransitionDisabledReason(
  current: AppointmentWorkflowStatus,
  target: AppointmentWorkflowStatus,
): string | undefined {
  if (current === target) return undefined;
  if (target !== 'requested') return undefined;
  if (current === 'requested') return undefined;
  return `Cannot change from ${WORKFLOW_LABELS[current]} to ${WORKFLOW_LABELS.requested}`;
}

export function getAppointmentWorkflowStatusOptions(
  appointment: Appointment,
): AppointmentWorkflowStatusOption[] {
  return getAppointmentWorkflowStatusOptionsForCurrent(getAppointmentWorkflowStatus(appointment));
}

export function getAppointmentWorkflowStatusOptionsForCurrent(
  current: AppointmentWorkflowStatus,
): AppointmentWorkflowStatusOption[] {
  return ALL_WORKFLOW_STATUSES.map((value) => ({
    value,
    label: WORKFLOW_LABELS[value],
    description: WORKFLOW_DESCRIPTIONS[value],
    disabled: getAppointmentTransitionDisabledReason(current, value) != null,
  }));
}

export function isLateCancelAppointment(appointment: Appointment): boolean {
  return getAppointmentWorkflowStatus(appointment) === 'late-cancelled';
}

export function isNoShowAppointment(appointment: Appointment): boolean {
  return getAppointmentWorkflowStatus(appointment) === 'no-show';
}

export function getOutcomeSpecificNoteTemplateKind(
  appointment: Appointment,
): 'late-cancel' | 'no-show' | 'cancelled' | undefined {
  const status = getAppointmentWorkflowStatus(appointment);
  if (status === 'late-cancelled') return 'late-cancel';
  if (status === 'no-show') return 'no-show';
  // Expired intake holds never became sessions, so there is nothing to document.
  if (status === 'cancelled' && getAppointmentCancelationReasonCode(appointment) !== 'reservation-expired') {
    return 'cancelled';
  }
  return undefined;
}

/** Apply per-client attendance statuses from group session edit (match by participant display name). */
export function applyGroupClientStatusesToAppointment(
  appointment: Appointment,
  clientStatuses: ReadonlyArray<{ name: string; status: string }>,
): Appointment {
  const participants = [...(appointment.participant ?? [])];
  for (const client of clientStatuses) {
    const idx = participants.findIndex(
      (p) =>
        p.actor?.reference?.startsWith('Patient/')
        && (p.actor?.display?.trim() ?? '') === client.name.trim(),
    );
    if (idx < 0) continue;
    const participant: AppointmentParticipant = { ...participants[idx]! };
    setParticipantAttendanceStatus(participant, client.status);
    participants[idx] = participant;
  }
  return { ...appointment, participant: participants };
}

/** True when appointment has multiple billed patient participants (group session). */
export function isGroupAppointment(appointment: Appointment): boolean {
  return getPatientParticipants(appointment).length > 1;
}
